import { takeEvery, call, select } from 'redux-saga/effects'
import Cookies from 'js-cookie'
import { postSyncari } from '@common/services'
import { MarketoPayload } from '@common/state/marketing/state'
import { MARKETO_PLAN_TYPE, COOKIE_NAMES, LANGUAGE_CODES } from '@common/constants'
import { buildSyncariPayload, preprocessMarketoPayload } from '@common/utils/aws-utils'
import { marketingActions, MarketingActionsTypes } from '@common/state/marketing/actions'
import { getLocationState, getMarketingState } from '@common/state/selectors'
import { LocationState } from '@common/state/location/state'

export function* postToMarketoSaga(action: ReturnType<typeof marketingActions.postToMarketo>) {
    try {
        // Determine whether we are sending to the AWS API (applies to legacy flows)
        // or just saving to sessionStorage for use after returning from OnePay (applies to unified checkout OnePay flow)
        const { saveToLocalStorage } = action.payload

        // eslint-disable-next-line camelcase
        const {
            phone,
            address,
            city,
            state: userState,
            zipcode,
            userKey,
            accountKey,
            ...rest
        } = action.payload.marketoUserInfo
        // grab is optOut value

        const { isUserOptOut } = yield select(getMarketingState)
        const {
            locationInfo: { country },
        }: LocationState = yield select(getLocationState)

        // grab language from URL if its valid
        const urlSplitForLanguage = window.location.pathname.split('/')[1]
        const isValidLanguageCode = Object.values(LANGUAGE_CODES).includes(urlSplitForLanguage)
        const siteLanguage = isValidLanguageCode ? urlSplitForLanguage : LANGUAGE_CODES.English

        // if no address, the user is trying
        const planType = zipcode ? MARKETO_PLAN_TYPE.BUY : MARKETO_PLAN_TYPE.TRIAL
        const payload: MarketoPayload = {
            mkto_trk: Cookies.get(COOKIE_NAMES.MARKETO_TRK) || '',
            lmi_ma: Cookies.get(COOKIE_NAMES.LMI_MA) || '',
            chop_id: Cookies.get(COOKIE_NAMES.CHOP_ID) || '',
            gclid: Cookies.get(COOKIE_NAMES.GCL_ID) || Cookies.get(COOKIE_NAMES.GCL_ID_2) || '',
            country,
            language: siteLanguage,
            phone: phone || '',
            planType,
            sourceUrl: window.location.href,
            address: address || '',
            city: city || '',
            state: userState || '',
            zipcode: zipcode || '',
            optin: isUserOptOut ? '0' : '1',
            userKey,
            accountKey,
            ...rest,
        }

        const prePayload = preprocessMarketoPayload(payload)

        const syncariPayload = buildSyncariPayload(prePayload)

        if (saveToLocalStorage) {
            // For OnePay flow, save to sessionStorage so it can be retrieved and posted to AwS after returning from OnePay
            window.sessionStorage.setItem('syncariPayload', JSON.stringify(syncariPayload))
        } else {
            // For CyberSource flow, post directly to AWS
            yield call(postSyncari, syncariPayload)
        }
    } catch (err: any) {
        console.error(err.message)
    }
}

// This gets called after returning from OnePay and landing on the Confirmation page
// See src/products/buy/views/Confirmation/index.tsx
export function* postSavedMarketoToAWS() {
    try {
        // Get the Marketo data from sessionStorage
        const syncariPayload = JSON.parse(window.sessionStorage.getItem('syncariPayload') || 'null')

        if (syncariPayload) {
            // Post it to AWS
            yield call(postSyncari, syncariPayload)

            // Remove from sessionStorage so if the user reloads the Confirmation screen, they don't get submitted again
            window.sessionStorage.removeItem('syncariPayload')
        }
        yield call(postSyncari, syncariPayload)
    } catch (err: any) {
        console.error(err.message)
    }
}

function* initializePostToMarketoSaga() {
    yield takeEvery(MarketingActionsTypes.POST_MARKETO, postToMarketoSaga)
    yield takeEvery(MarketingActionsTypes.POST_MARKETO_SESSION_DATA_TO_AWS, postSavedMarketoToAWS)
}

export default initializePostToMarketoSaga
